import { ResumeStats } from '../models/ResumeStats.js';
import { PRDetector } from './PRDetector.js';

/**
 * StatsCalculator — derives ResumeStats from an array of RaceEntry instances.
 *
 * Pure service, no Firebase dependencies.
 */
export class StatsCalculator {
  /**
   * @param {RaceEntry[]} races
   * @returns {ResumeStats}
   */
  static calculate(races) {
    if (!races || races.length === 0) {
      return new ResumeStats();
    }

    // Exclude DNF and untimed races from distance, elevation, and pace calculations
    const isDNF = (r) => r.time && r.time.toUpperCase() === 'DNF';
    const isUntimed = (r) => r.isTimed === false;
    const finishedRaces = races.filter((r) => !isDNF(r) && !isUntimed(r));

    const totalDistance = finishedRaces.reduce((sum, r) => sum + (r.distance || 0), 0);

    const totalElevation = finishedRaces
      .filter((r) => r.type === 'trail' && r.elevation != null)
      .reduce((sum, r) => sum + r.elevation, 0);

    // Average pace: only finished races with valid time AND positive distance
    const racesWithPace = finishedRaces.filter(
      (r) => r.timeInSeconds != null && r.distance && r.distance > 0
    );

    let avgPace = null;
    if (racesWithPace.length > 0) {
      const totalTime = racesWithPace.reduce((sum, r) => sum + r.timeInSeconds, 0);
      const totalDist = racesWithPace.reduce((sum, r) => sum + r.distance, 0);
      avgPace = totalDist > 0 ? totalTime / totalDist : null;
    }

    // Race count includes all races (DNF still counts as a race entered)
    const raceCount = races.length;
    const highlightedCount = races.filter((r) => r.isHighlighted).length;
    const prByDistance = PRDetector.detect(races);

    return new ResumeStats({
      totalDistance,
      totalElevation,
      avgPace,
      raceCount,
      prByDistance,
      highlightedCount,
    });
  }
}
